import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircularProgress, Backdrop, useTheme } from "@mui/material";
import axios from "axios";
import backend from "../../../../api";
import Classes from "./Profile.module.css";
import { GoalIcon, ProfileIcon } from "../../../../utils/Icons";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../../redux-store/auth/actions";
import BasicInfo from "./Comp/BasicInfo";
import Preferences from "./Comp/Preferences";
import Extras from "./Comp/Extras";
import GoalsAndInterests from "./Comp/GoalsAndInterests";
import { useSnackbar } from "notistack";

const STORAGE_KEY = "MenteeProfileDraft";
const slideVariants = { initial: { x: 10, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -10, opacity: 0 } };

const blankMentee = {
  basicInfo: { name: "", title: "", education: "", clubs: "", bio: "", avatar: null, avatarPreview: null },
  GoalsAndInterests: { goals: "", lookingFor: "", interest: [] },
  Preferences: { mentorLevel: [], languagesSpoken: [], availability: [] },
  Extras: { linkedinUrl: "", portfolioUrl: "", files: [], resume: "" },
};

export default function Mentee() {
  const { user } = useSelector((s) => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;
  const dispatch = useDispatch();
  const theme = useTheme();

  const [activePage, setActivePage] = useState(1);
  const [MenteeData, setMenteeData] = useState(blankMentee);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ---------- hydrate from draft or backend ---------- */
  useEffect(() => {
    const draft = localStorage.getItem(STORAGE_KEY);
    if (draft) {
      try {
        const { MenteeData: d, activePage: p } = JSON.parse(draft);
        if (d) setMenteeData((prev) => ({ ...prev, ...d }));
        if (p) setActivePage(p);
        return;
      } catch { /* ignore */ }
    }
    if (user?.menteeProfile) {
      // accept either camelCase or Capitalized section names from backend
      const mp = user.menteeProfile;
      setMenteeData((p) => ({
        ...p,
        basicInfo: mp.basicInfo ?? p.basicInfo,
        GoalsAndInterests: mp.goalsAndInterests ?? mp.GoalsAndInterests ?? p.GoalsAndInterests,
        Preferences: mp.preferences ?? mp.Preferences ?? p.Preferences,
        Extras: mp.extras ?? mp.Extras ?? p.Extras,
      }));
    }
  }, [user]);

  /* ---------- persist draft (strip File/blobs) ---------- */
  useEffect(() => {
    const safe = (state) => {
      const replacer = (_, v) => (v instanceof File || v?.startsWith?.("blob:") ? undefined : v);
      return JSON.parse(JSON.stringify(state, replacer));
    };
    const id = requestAnimationFrame(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ MenteeData: safe(MenteeData), activePage }));
    });
    return () => cancelAnimationFrame(id);
  }, [MenteeData, activePage]);

  /* ---------- helpers ---------- */
  const sanitizeForInterimSave = useCallback((data) => {
    // don’t send File objects during step-to-step saves
    const copy = JSON.parse(JSON.stringify(data));
    if (copy?.basicInfo) {
      if (copy.basicInfo.avatar && typeof copy.basicInfo.avatar !== "string") copy.basicInfo.avatar = undefined;
      delete copy.basicInfo.avatarPreview;
    }
    if (copy?.Extras) {
      copy.Extras.files = []; // Files only uploaded on Finish
    }
    return copy;
  }, []);

  const saveProfile = useCallback(
    async (data, markOnboarded = false) => {
      setSaving(true);
      try {
        const payload = {
          onboarded: markOnboarded ? true : user?.onboarded,
          menteeProfile: sanitizeForInterimSave(data),
        };
        const res = await backend.put(`/api/user/${userId}`, payload);
        const updatedUser = { ...user, ...res.data.data };
        await dispatch(setUserData(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    },
    [dispatch, user, userId, sanitizeForInterimSave]
  );

  const goNext = useCallback(async () => {
    await saveProfile(MenteeData, false);
    setActivePage((p) => Math.min(p + 1, 4));
  }, [MenteeData, saveProfile]);

  const goBack = () => setActivePage((p) => Math.max(p - 1, 1));
  const goStep = async (id) => { await saveProfile(MenteeData, false); setActivePage(id); };

  const updateSection = useCallback((section, partial) => {
    setMenteeData((prev) => ({ ...prev, [section]: { ...prev[section], ...partial } }));
  }, []);

  const uploadMedia = async (files) => {
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const res = await axios.post("https://mentr-aws-server-668d71936193.herokuapp.com/api/deck/upload", fd);
      return res.data.data[0]?.imgurl;
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Finish (uploads + normalized payload) ---------- */
  const handleFinish = async () => {
    try {
      setSaving(true);

      let avatarUrl = "";
      if (MenteeData.basicInfo.avatar instanceof File) {
        avatarUrl = await uploadMedia([MenteeData.basicInfo.avatar]);
      } else if (typeof MenteeData.basicInfo.avatarPreview === "string") {
        avatarUrl = MenteeData.basicInfo.avatarPreview;
      } else if (typeof MenteeData.basicInfo.avatar === "string") {
        avatarUrl = MenteeData.basicInfo.avatar;
      }

      let resumeUrl = "";
      const pdfFile = MenteeData.Extras.files.find((f) => f instanceof File && f.type === "application/pdf");
      if (pdfFile) resumeUrl = await uploadMedia([pdfFile]);
      else if (typeof MenteeData.Extras.resume === "string") resumeUrl = MenteeData.Extras.resume;

      const cleanedBasicInfo = { ...MenteeData.basicInfo, avatar: avatarUrl };
      delete cleanedBasicInfo.avatarPreview;

      const cleanedExtras = { ...MenteeData.Extras, resume: resumeUrl, files: [] };

      const payload = {
        onboarded: true,
        menteeProfile: {
          basicInfo: cleanedBasicInfo,
          goalsAndInterests: { ...MenteeData.GoalsAndInterests },
          preferences: { ...MenteeData.Preferences },
          extras: cleanedExtras,
        },
      };

      const { data } = await backend.put(`/api/user/${userId}`, payload);
      const updatedUser = { ...user, ...data.data };
      dispatch(setUserData(updatedUser));
      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.removeItem(STORAGE_KEY);

      enqueueSnackbar("🎉 Profile completed!", { variant: "success" });
      window.location.replace("/");
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err.response?.data?.message || "Something went wrong", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const sideBarSteps = useMemo(
    () => [
      { id: 1, label: "Basic Info", icon: <ProfileIcon height="15" fill="white" /> },
      { id: 2, label: "Goals & Interests", icon: <GoalIcon height="17" fill="white" /> },
      { id: 3, label: "Preferences", icon: <img src="/assets/icons/session-details.png" height={17} width={17} alt="" /> },
      { id: 4, label: "Extras", icon: <img src="/assets/icons/extras.png" height={16} width={16} alt="" /> },
    ],
    []
  );

  const stepComponent = useMemo(
    () =>
    ({
      1: <BasicInfo data={MenteeData.basicInfo} update={(d) => updateSection("basicInfo", d)} onNext={goNext} />,
      2: (
        <GoalsAndInterests
          data={MenteeData.GoalsAndInterests}
          update={(d) => updateSection("GoalsAndInterests", d)}
          onNext={goNext}
          onBack={goBack}
        />
      ),
      3: (
        <Preferences
          data={MenteeData.Preferences}
          update={(d) => updateSection("Preferences", d)}
          onNext={goNext}
          onBack={goBack}
        />
      ),
      4: (
        <Extras
          data={MenteeData.Extras}
          update={(d) => updateSection("Extras", d)}
          onBack={goBack}
          onFinish={handleFinish}
        />
      ),
    }[activePage]),
    [MenteeData, goNext, goBack, handleFinish, updateSection, activePage]
  );

  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [activePage]);

  return (
    <>
      <Backdrop open={saving || uploading} sx={{ zIndex: theme.zIndex.tooltip + 1, color: "#fff", backdropFilter: "blur(2px)" }}>
        <CircularProgress color="inherit" thickness={4} />
      </Backdrop>

      <div className={Classes.hero}>
        <div className={Classes.header}><h5>Mentr</h5></div>

        <div className={Classes.body} ref={bodyRef}>
          <div className={Classes.bodyInner}>
            <motion.h3 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4 }}>
              Build Your Profile
            </motion.h3>
            <motion.h5 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4, delay: 0.05 }}>
              Don’t worry — you can edit this later in your settings.
            </motion.h5>

            <motion.div className={Classes.stepper} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: "tween", duration: 0.4, delay: 0.1 }}>
              {sideBarSteps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div className={`${Classes.sideElement} ${activePage === step.id ? Classes.current : ""}`}>
                    <div className={Classes.flexAlign10}>
                      <div className={`${Classes.imageCon} ${activePage >= step.id ? Classes.active : ""}`}>{step.icon}</div>
                      <h5 className={activePage >= step.id ? Classes.activeText : undefined}>{step.label}</h5>
                    </div>
                  </div>
                  {idx !== sideBarSteps.length - 1 && (
                    <div className={`${Classes.hr} ${activePage > step.id ? Classes.coloredHr : ""}`} />
                  )}
                </React.Fragment>
              ))}
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div key={activePage} variants={slideVariants} initial="initial" animate="animate" exit="exit" className={Classes.infoMain}>
                {stepComponent}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}
